import React, {type CSSProperties, forwardRef, useCallback, useImperativeHandle, useRef, useState} from 'react'
import {useParams} from 'react-router-dom';
import {default as VncViewer} from './VncViewer.tsx'
import NoVncClient from '@novnc/novnc/lib/rfb';
import {type DisconnectEvent, KeySymbolList} from "./types.ts";
import {instance_manager} from '../../lib/InstanceManager.ts';
import "../../static/novnc/styles/lite.css";
import imgCtrlAltDel from "../../static/novnc/images/ctrlaltdel.svg";
import imgClipboard from "../../static/novnc/images/clipboard.svg";
import {createWebsocketAddr, deleteSessionCookie, sessionToCookie} from "../../lib/api.ts";
import Loading from "../Loading.tsx";
import {swal_util} from "../../lib/SwalUtil.ts";
import bell from "../../assets/bell.ogg";
import {useTranslation} from "react-i18next";

// https://github.com/RostislavBerezhnoy/react-vnc-viewer/tree/develop/src

function requireShift(char: string) {
    const specialShiftChars = `~!@#$%^&*()_+{}|:"<>?`; // Characters that require Shift

    return specialShiftChars.includes(char) || (char >= 'A' && char <= 'Z');
}

function koreanToEnglish(input: string) {
    const INITIAL = [
        "r", "R", "s", "e", "E", "f", "a", "q", "Q", "t",
        "T", "d", "w", "W", "c", "z", "x", "v", "g"
    ];
    const MEDIAL = [
        "k", "o", "i", "O", "j", "p", "u", "P", "h", "hk",
        "ho", "hl", "y", "n", "nj", "np", "nl", "b", "m",
        "ml", "l"
    ];
    const FINAL = [
        "", "r", "R", "rt", "s", "sw", "sg", "e", "f", "fr",
        "fa", "fq", "ft", "fx", "fv", "fg", "a", "q", "qt",
        "t", "T", "d", "w", "c", "z", "x", "v", "g"
    ];

    const HANGUL_BASE = 0xAC00;
    const INITIAL_BASE = 588;
    const MEDIAL_BASE = 28;

    // 자모 분리 테이블 (단독 자모 입력 처리)
    const JAMO_INITIAL = [
        "ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ", "ㅅ",
        "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"
    ];
    const JAMO_MEDIAL = [
        "ㅏ", "ㅐ", "ㅑ", "ㅒ", "ㅓ", "ㅔ", "ㅕ", "ㅖ", "ㅗ", "ㅘ",
        "ㅙ", "ㅚ", "ㅛ", "ㅜ", "ㅝ", "ㅞ", "ㅟ", "ㅠ", "ㅡ", "ㅢ", "ㅣ"
    ];
    const JAMO_FINAL = [
        "ㄱ", "ㄲ", "ㄳ", "ㄴ", "ㄵ", "ㄶ", "ㄷ", "ㄹ", "ㄺ", "ㄻ",
        "ㄼ", "ㄽ", "ㄾ", "ㄿ", "ㅀ", "ㅁ", "ㅂ", "ㅄ", "ㅅ", "ㅆ",
        "ㅇ", "ㅈ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"
    ];

    let result = "";

    for (const char of input) {
        const code = char.charCodeAt(0);

        if (code >= HANGUL_BASE && code <= 0xD7A3) {
            // 완성된 한글 처리
            const syllableIndex = code - HANGUL_BASE;

            const initialIndex = Math.floor(syllableIndex / INITIAL_BASE);
            const medialIndex = Math.floor(
                (syllableIndex % INITIAL_BASE) / MEDIAL_BASE
            );
            const finalIndex = syllableIndex % MEDIAL_BASE;

            result += INITIAL[initialIndex];
            result += MEDIAL[medialIndex];
            result += FINAL[finalIndex];
        } else if (JAMO_INITIAL.includes(char)) {
            // 초성 단독 처리
            const initialIndex = JAMO_INITIAL.indexOf(char);
            result += INITIAL[initialIndex];
        } else if (JAMO_MEDIAL.includes(char)) {
            // 중성 단독 처리
            const medialIndex = JAMO_MEDIAL.indexOf(char);
            result += MEDIAL[medialIndex];
        } else if (JAMO_FINAL.includes(char)) {
            // 종성 단독 처리
            const finalIndex = JAMO_FINAL.indexOf(char);
            result += FINAL[finalIndex];
        } else {
            // 한글이 아니거나 매핑되지 않은 문자는 그대로 추가
            result += char;
        }
    }

    return result;
}

const CharType = {
    ALPHA: 0,
    NON_ALPHA_ASCII: 1,
    KOREAN: 2,
    UNKNOWN: 3,
}

type ValueOf<T> = T[keyof T];
type CharTypeVal = ValueOf<typeof CharType>

/**
 * 유니코드 값을 확인하여 지정된 문자의 유형을 결정합니다.
 *
 * @param {string} char - 확인할 단일 문자입니다.
 * @return {0|1|2|-1} 문자가 알파벳인 경우 0, 알파벳이 아닌 ASCII 문자는 1, 한글은 2, 지원불가는 -1
 */
function detectCharType(char: string): CharTypeVal {
    const HANGUL_START = 0xAC00; // 한글 시작
    const HANGUL_END = 0xD7A3;   // 한글 끝
    const ASCII_START = 0x00;    // ASCII 시작
    const ASCII_END = 0x7F;      // ASCII 끝

    const code = char.charCodeAt(0);

    if (code >= 0x41 && code <= 0x5A || code >= 0x61 && code <= 0x7A) {
        return CharType.ALPHA; // 알파벳
    }

    if (code >= ASCII_START && code <= ASCII_END) {
        return CharType.NON_ALPHA_ASCII; // 알파벳이 아닌 ASCII 문자
    }

    if (code >= HANGUL_START && code <= HANGUL_END) {
        return CharType.KOREAN; // 한글
    }

    return CharType.UNKNOWN; // 기타 문자
}

const VncConsoleLoading = forwardRef<HTMLDivElement, { style?: CSSProperties }>(
    ({style}, ref) => {
        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => divRef.current!)

        return (
            <div className="vnc-loading" ref={divRef} style={style}>
                <Loading text="Loading..."/>
            </div>
        )
    }
);

export function VncConsole() {
    const {t} = useTranslation();
    const {id} = useParams<{ id: string }>();
    const [desktopName, setDesktopName] = useState<string>("");
    const [statusText, setStatusText] = useState<string>("");
    const [title, setTitle] = useState<string>("Loading");
    const [serverName, setServerName] = useState<string>("");
    const [statusClass, setStatusClass] = useState<string>("noVNC_status_warn");
    const statusBarRef = useRef<HTMLDivElement>(null);
    const vncScreenRef = useRef<React.ElementRef<typeof VncViewer>>(null);
    const [numLockActiveClass, setNumLockActiveClass] = useState("");
    const [capsLockActiveClass, setCapsLockActiveClass] = useState("");
    const [scrollLockActiveClass, setScrollLockActiveClass] = useState("");
    const audioRef = useRef<HTMLAudioElement>(null);

    const onKeyUpDown = useCallback((e: KeyboardEvent) => {
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState
        const numLockState = e.getModifierState('NumLock');
        const capsLockState = e.getModifierState('CapsLock');
        const scrollLockState = e.getModifierState('ScrollLock');

        setNumLockActiveClass(numLockState ? "on" : "");
        setCapsLockActiveClass(capsLockState ? "on" : "");
        setScrollLockActiveClass(scrollLockState ? "on" : "");
    }, []);

    // const onKeyDown = useCallback((e: KeyboardEvent) => {
    //     const numLockState = e.getModifierState('NumLock');
    //     const capsLockState = e.getModifierState('CapsLock');
    //     const scrollLockState = e.getModifierState('ScrollLock');
    //
    //     setNumLockActiveClass(numLockState ? "on" : "");
    //     setCapsLockActiveClass(capsLockState ? "on" : "");
    //     setScrollLockActiveClass(scrollLockState ? "on" : "");
    // }, []);

    function setStatus(title: string, status: "normal" | "warn" | "error" = "warn") {
        setTitle(title);
        setStatusClass(`noVNC_status_${status}`);
    }

    function sendChar(char: string) {
        const {rfb} = vncScreenRef.current ?? {}
        if (!rfb) return;

        if (char == "\n") {
            rfb.sendKey(KeySymbolList.XK_Return, null, true);
            rfb.sendKey(KeySymbolList.XK_Return, null, false);
        } else if (char == "\t") {
            rfb.sendKey(KeySymbolList.XK_Tab, null, true);
            rfb.sendKey(KeySymbolList.XK_Tab, null, false);
        } else if (char == "\b") {
            rfb.sendKey(KeySymbolList.XK_BackSpace, null, true);
            rfb.sendKey(KeySymbolList.XK_BackSpace, null, false);
        } else if (char == "\r") {
            return
        }

        let shift = false;
        if (requireShift(char)) {
            rfb.sendKey(KeySymbolList.XK_Shift_L, null, true);
            shift = true;
        }

        rfb.sendKey(char.charCodeAt(0), null);
        if (shift) rfb.sendKey(KeySymbolList.XK_Shift_L, null, false);
    }

    function releaseModifiers(rfb: NoVncClient | undefined) {
        if (!rfb) return;

        rfb.sendKey(KeySymbolList.XK_Control_L, null, false);
        rfb.sendKey(KeySymbolList.XK_Control_R, null, false);

        rfb.sendKey(KeySymbolList.XK_Alt_L, null, false);
        rfb.sendKey(KeySymbolList.XK_Alt_R, null, false);

        rfb.sendKey(KeySymbolList.XK_Shift_L, null, false);
        rfb.sendKey(KeySymbolList.XK_Shift_R, null, false);
    }

    const eventHandlers = {
        async onClickPasteButton(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
            try {
                const {rfb} = vncScreenRef.current ?? {}
                if (!rfb) return;
                rfb.focus();
                const chars: string[] = (await navigator.clipboard.readText()).split('');

                if (chars.length == 0) {

                    await swal_util.show_warning(t("vncConsole.thereIsNothingToPasteToTheClipboard"));
                    return;
                }
                const isPressCtrl = e.ctrlKey;
                if (isPressCtrl) {
                    const prompt = await swal_util.show_question(
                        t("vncConsole.checkPasteOperation"),
                        t("vncConsole.pasteTheContentsOfTheClipboardTheEnterKeyIsEnteredAsASuffix")
                    );
                    chars.push("\n");
                    if(!prompt.isConfirmed){
                        return;
                    }
                }

                releaseModifiers(rfb);

                const charTypes = Array.from(new Set(chars.map(char => detectCharType(char))));
                if (charTypes.indexOf(CharType.UNKNOWN) != -1) {
                    await swal_util.show_error(t("vncConsole.containsUnsupportedCharacters"));
                    setStatusText("");
                    return;
                }

                const alphaIdx = charTypes.indexOf(CharType.ALPHA);
                const koreanIdx = charTypes.indexOf(CharType.KOREAN);

                if (alphaIdx != -1 && koreanIdx != -1) {
                    await swal_util.show_error(t("vncConsole.englishAndKoreanMustNotBeCombined"));
                    setStatusText("");
                    return;
                }

                if (koreanIdx != -1) {
                    await swal_util.show_info(t("vncConsole.changeTheRemoteLinuxKeyboardToTheKoreanKeyboard"));
                }

                // e.ctrlKey
                setStatusText("- Pasting text... ");

                const lastIdx = chars.length - 1;
                chars.forEach((char, index) => {
                    setTimeout(() => {
                        if (detectCharType(char) == CharType.KOREAN) {
                            const korean = koreanToEnglish(char)
                            korean.split('').forEach(k => sendChar(k));
                        } else {
                            sendChar(char);
                        }

                        if (lastIdx == index) {
                            setStatusText("");
                        }
                    }, index * 25);
                });
            } catch (err) {
                console.error("Failed to read text from clipboard: ", err);
            }

        },
        onClickRebootBtn() {
            const {rfb} = vncScreenRef.current ?? {}
            rfb?.sendCtrlAltDel();
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onConnect(_noVncClient: NoVncClient | undefined) {
            setStatus("Connected to", "normal");
            deleteSessionCookie();
        },
        onDisconnect(_noVncClient: NoVncClient | undefined, e: DisconnectEvent | undefined) {
            deleteSessionCookie();
            if (e!.detail.clean) {
                setStatus("Disconnected", "normal");
            } else {
                setStatus("Something went wrong, connection is closed", "error");
            }
        },
        onChangeTty(e: React.ChangeEvent<HTMLSelectElement>) {
            if (e.target.options[0].selected) return;
            const value = parseInt(e.target.value, 10);

            const {rfb} = vncScreenRef.current ?? {}
            if (!rfb) return;

            releaseModifiers(rfb);

            const functionKeyCode = KeySymbolList.XK_F1 - 1 + value;
            setTimeout(() => {
                rfb?.sendKey(KeySymbolList.XK_Control_L, null, true);
                rfb?.sendKey(KeySymbolList.XK_Alt_L, null, true);
                rfb?.sendKey(functionKeyCode, null, true);

                rfb?.sendKey(KeySymbolList.XK_Control_L, null, false);
                rfb?.sendKey(KeySymbolList.XK_Alt_L, null, false);
                rfb?.sendKey(functionKeyCode, null, false);

                e.target.options[0].selected = true;
            }, 25);
        }
    }

    sessionToCookie();
    document.body.classList.add('no-vnc');

    if (!serverName) {
        instance_manager.getInstanceDetail(id ?? "-1").then((e) => {
            setServerName(e!.data["data"]["name"]);
        });
    }

    function playBell() {
        if (!audioRef.current) return;
        audioRef.current.play().finally();
    }

    return (
        <>
            <div id="noVNC_status_bar" className={statusClass} ref={statusBarRef}>
                <div id="noVNC_left_dummy_elem">
                    <i className={`vnc numlock ${numLockActiveClass}`}></i>
                    <i className={`vnc caplock ${capsLockActiveClass}`}></i>
                    <i className={`vnc scrollock ${scrollLockActiveClass}`}></i>
                </div>
                <div id="noVNC_status">{serverName} - {title} {desktopName} {statusText}</div>
                <div id="noVNC_buttons">
                    <select onChange={(e) => eventHandlers.onChangeTty(e)}>
                        <option value="-1">TTY Selector</option>
                        <option value="1">Ctrl + Alt + F1</option>
                        <option value="2">Ctrl + Alt + F2</option>
                        <option value="3">Ctrl + Alt + F3</option>
                        <option value="4">Ctrl + Alt + F4</option>
                        <option value="5">Ctrl + Alt + F5</option>
                        <option value="6">Ctrl + Alt + F6</option>
                        <option value="7">Ctrl + Alt + F7</option>
                        <option value="8">Ctrl + Alt + F8</option>
                        <option value="9">Ctrl + Alt + F9</option>
                        <option value="10">Ctrl + Alt + F10</option>
                        <option value="11">Ctrl + Alt + F11</option>
                        <option value="12">Ctrl + Alt + F12</option>
                    </select>

                    <button onClick={(e) => eventHandlers.onClickPasteButton(e)}>
                        <img
                            src={imgClipboard}
                            alt={t("vncConsole.enterClipboardTextStringIntoRemote")}
                            title={t("vncConsole.enterClipboardTextStringIntoRemote")}
                        />
                    </button>

                    &nbsp;

                    <button onClick={() => eventHandlers.onClickRebootBtn()}>
                        <img src={imgCtrlAltDel} alt="Ctrl + Alt + Del" title="Ctrl + Alt + Del"/>
                    </button>
                </div>
            </div>
            <audio ref={audioRef} src={bell}/>
            <VncViewer
                ref={vncScreenRef}
                url={createWebsocketAddr(`/instance/vnc/${id}`)}
                className="novnc-screen"
                // 디버그시 아래 debug flag 활성
                // debug
                scaleViewport={true}
                maxRetryCount={3}
                showDotCursor={true}
                resizeSession={true}
                clipViewport={true}
                rfbOptions={
                    {shared: true,}
                }
                loader={<VncConsoleLoading style={{
                    height: `calc(100vh - ${statusBarRef.current?.offsetHeight ?? 0}px)`,
                }}/>}
                onConnect={(noVncClient) => eventHandlers.onConnect(noVncClient)}
                onDisconnect={(noVncClient, e) => eventHandlers.onDisconnect(noVncClient, e)}
                onDesktopName={(e) => setDesktopName(e?.detail?.name ?? 'Unknown')}
                onKeyUp={onKeyUpDown}
                onKeyDown={onKeyUpDown}
                onBell={() => playBell()}
            />
        </>
    )
}


export default VncConsole;